import { useModal } from 'components/modals/useModal';
import { Button } from 'components/ui/button';
import HoursDisplay from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import PercentageDisplay from 'components/util/PercentageDisplay';
import { useSharedPayment } from 'pages/invoices/payers/useSharedPayment';
import { mapGroupingInvoiceRowEditFormToUpdateDto } from 'pages/invoices/util/InvoicesPageUtil';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useUpdateGroupingInvoiceRowMutation } from 'store/api/invoices';
import { GroupingInvoiceRowEditForm, InvoiceData, InvoiceTimecardTotals } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';
import { GroupingInvoiceRowEditModal } from './GroupingInvoiceRowEditModal';
import { TOTALS_CAPTION, TOTALS_GRID, TOTALS_ROW, TOTALS_TEXT, totalsButtonStyle } from './TotalsTableCard';
import { DIALOG_480 } from 'components/modals/dialogWidths';

const FORM_OVERFLOW_CUSTOM_STYLE = {
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
};

interface Props {
    invoiceTotals?: InvoiceTimecardTotals;
    invoice: InvoiceData;
    isInViewMode: boolean;
    matterId: number;
}

const InvoiceTimecardTotalsTableItem: React.FC<Props> = (props) => {
    const { invoiceTotals, invoice, isInViewMode, matterId } = props;
    const { openModal, closeModal } = useModal();
    const { isPaymentCurrencyMode } = usePaymentCurrencyMode(invoice);
    const { isSharedPaymentCopy } = useSharedPayment(invoice);
    const [updateGroupingInvoiceRow] = useUpdateGroupingInvoiceRowMutation();

    // discount may be positive for split invoices, negative for regular invoices
    const amountAdjustmentDiscount = invoiceTotals?.amountAdjustmentDiscount || 0;
    const discountRate = invoiceTotals?.discountRate || 0;

    const handleUpdateTotals = useCallback(
        async (data: GroupingInvoiceRowEditForm) => {
            await updateGroupingInvoiceRow(mapGroupingInvoiceRowEditFormToUpdateDto(data, invoice.id));
        },
        [invoice.id, updateGroupingInvoiceRow]
    );

    const handleOpenTotalsEditModal = useCallback(() => {
        // The wide layout holds the amount fields, which a shared-payment copy's modal leaves out.
        const isSplitInvoice = (invoice.isSplit ?? false) && !isSharedPaymentCopy;
        const totalAmount = isPaymentCurrencyMode
            ? (invoiceTotals?.itemTotalInPaymentCurrency ?? 0)
            : (invoiceTotals?.totalAfterAdjustments ?? 0);
        const amountBeforeDiscount = isPaymentCurrencyMode
            ? (invoiceTotals?.amountBeforeDiscountInPaymentCurrency ?? 0)
            : (invoiceTotals?.totalBeforeAdjustments ?? 0);
        const discountAmt = isPaymentCurrencyMode
            ? amountBeforeDiscount - totalAmount
            : (invoiceTotals?.amountAdjustmentDiscount ?? 0);
        openModal(
            <GroupingInvoiceRowEditModal
                onClose={closeModal}
                titleId={'invoices.totals-tab.update-fee'}
                initialData={{
                    totalAmount,
                    vatRate: invoiceTotals?.vatRate ?? 0,
                    amountBeforeDiscount,
                    isDeleted: false,
                    discountRate: Math.abs(invoiceTotals?.discountRate ?? 0),
                    discountAmount: discountAmt ? (Math.abs(discountAmt) * -1).toFixed(2) : '0',
                    description: invoiceTotals?.description ?? '',
                    groupingRowId: invoiceTotals?.groupingRowId ?? '',
                    sourceType: 'TIMECARD',
                    matterId,
                }}
                handleUpdateTotals={(data) => {
                    void handleUpdateTotals(data);
                }}
                invoice={invoice}
            />,
            closeModal,
            { ...FORM_OVERFLOW_CUSTOM_STYLE, width: isSplitInvoice ? '700px' : '500px' },
            undefined,
            isSplitInvoice ? undefined : DIALOG_480
        );
    }, [
        closeModal,
        handleUpdateTotals,
        invoice,
        invoiceTotals,
        isPaymentCurrencyMode,
        isSharedPaymentCopy,
        matterId,
        openModal,
    ]);

    return (
        <div className={TOTALS_ROW}>
            <div className={TOTALS_GRID}>
                <div className="col-span-3 flex flex-col gap-2">
                    <p className={cn(TOTALS_TEXT, 'font-semibold')}>{invoiceTotals?.description || ''}</p>
                    <div className={cn(TOTALS_CAPTION, 'flex gap-2')}>
                        <span className="flex gap-[0.5ch]">
                            {t('invoices.totals-tab.table-contents.total-hours')}
                            <span className="font-semibold">
                                <HoursDisplay value={invoiceTotals?.totalHours || 0} />
                            </span>
                        </span>
                        <span className="flex gap-[0.5ch]">
                            {t('invoices.totals-tab.table-contents.total-before-adjustments')}
                            <span className="font-semibold">
                                <MoneyDisplay amount={invoiceTotals?.totalBeforeAdjustments || 0} />
                            </span>
                        </span>
                    </div>
                </div>
                <div className={cn(TOTALS_TEXT, 'col-span-1 flex flex-col gap-2 text-right')}>
                    <p>
                        <MoneyDisplay
                            amount={
                                amountAdjustmentDiscount > 0 ? amountAdjustmentDiscount * -1 : amountAdjustmentDiscount
                            }
                        />
                    </p>
                    <p className="text-fg-secondary">
                        <PercentageDisplay value={discountRate > 0 ? discountRate * -1 : discountRate} />
                    </p>
                </div>
                <div className={cn(TOTALS_TEXT, 'col-span-2 flex flex-col gap-2 text-right')}>
                    <p>
                        <MoneyDisplay amount={invoiceTotals?.totalAfterAdjustments || 0} />
                    </p>
                    <div className="h-[18px]" />
                </div>
                <div className={cn(TOTALS_TEXT, 'col-span-1 flex flex-col gap-2 text-right')}>
                    <p>
                        <MoneyDisplay amount={invoiceTotals?.vatAmount || 0} />
                    </p>
                    <p className="text-fg-secondary">
                        <PercentageDisplay value={(invoiceTotals?.vatRate || 0) * 100} />
                    </p>
                </div>
                <div className={cn(TOTALS_TEXT, 'col-span-2 flex flex-col gap-2 text-right font-semibold')}>
                    <p>
                        <MoneyDisplay amount={invoiceTotals?.totalAfterAdjustmentsWithVat || 0} />
                    </p>
                    <div className="h-[18px]" />
                </div>
                <div className={cn(TOTALS_TEXT, 'col-span-2 flex flex-col gap-2 text-right font-semibold')}>
                    <p>
                        <MoneyDisplay amount={invoiceTotals?.totalWithVatPaymentCurrency || 0} />
                    </p>
                    <div className="h-[18px]" />
                </div>
                <div className="col-span-1 flex flex-col items-end gap-2">
                    {!isInViewMode ? (
                        <Button {...totalsButtonStyle('w-[45px]')} onClick={handleOpenTotalsEditModal}>
                            <FormattedMessage id="invoices.totals-tab.table-contents.edit-button" />
                        </Button>
                    ) : (
                        <div />
                    )}
                    <div className="h-[19px]" />
                </div>
            </div>
        </div>
    );
};

export default InvoiceTimecardTotalsTableItem;
